<template>
  <div class="space-y-4 mt-4">
    <!-- Emailable Contacts -->
    <div v-if="hasEmailAddresses" class="mb-6">
      <h4 class="text-lg font-medium text-gray-700 mb-3">Emailable Contacts</h4>
      <div class="space-y-4">
        <!-- Organization Email -->
        <div v-if="organizationEmail" class="space-y-2">
          <div class="flex items-center gap-2 p-2 bg-gray-50 rounded">
            <i class="pi pi-building text-gray-600"></i>
            <span class="text-sm">{{ organizationEmail }}</span>
            <div class="ml-auto flex gap-2">
              <Button
                icon="pi pi-envelope"
                label="Compose"
                text
                size="small"
                @click="openEmailDialog('organization', props.organizationId || '')"
              />
              <Button
                icon="pi pi-list"
                label="Subscriptions"
                text
                size="small"
                @click="openSubscriptionDialog('organization', props.organizationId || '')"
              />
            </div>
          </div>
          <!-- Organization Subscriptions -->
          <div
            v-if="organizationSubscriptions.filter((sub) => sub.status === 'active').length > 0"
            class="ml-6 space-y-1"
          >
            <div
              v-for="sub in organizationSubscriptions.filter((sub) => sub.status === 'active')"
              :key="sub.id"
              class="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border"
            >
              <span class="text-sm">{{ getListName(sub.listId) }}</span>
              <span class="text-xs text-gray-500">({{ sub.id }})</span>
              <Tag
                :value="sub.status"
                :severity="sub.status === 'active' ? 'success' : 'warning'"
                class="text-xs"
              />
            </div>
          </div>
        </div>
        <!-- Person Emails -->
        <div v-for="person in emailablePeople" :key="person.id" class="space-y-2">
          <div class="flex items-center gap-2 p-2 bg-gray-50 rounded">
            <i class="pi pi-user text-gray-600"></i>
            <span class="text-sm">{{ person.firstName }} {{ person.lastName }}</span>
            <span class="text-gray-500">({{ person.email }})</span>
            <div class="ml-auto flex gap-2">
              <Button
                icon="pi pi-envelope"
                label="Compose"
                text
                size="small"
                @click="openEmailDialog('person', person.id)"
              />
              <Button
                icon="pi pi-list"
                label="Subscriptions"
                text
                size="small"
                @click="openSubscriptionDialog('person', person.id)"
              />
            </div>
          </div>
          <!-- Person Subscriptions -->
          <div
            v-if="
              personSubscriptions[person.id]?.filter((sub) => sub.status === 'active').length > 0
            "
            class="ml-6 space-y-1"
          >
            <div
              v-for="sub in personSubscriptions[person.id]?.filter(
                (sub) => sub.status === 'active',
              )"
              :key="sub.id"
              class="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border"
            >
              <span class="text-sm">{{ getListName(sub.listId) }}</span>
              <span class="text-xs text-gray-500">({{ sub.id }})</span>
              <Tag
                :value="sub.status"
                :severity="sub.status === 'active' ? 'success' : 'warning'"
                class="text-xs"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="hasEmailAddresses">
      <!-- Email History -->
      <div class="mb-4">
        <h4 class="text-lg font-medium text-gray-700">Email History</h4>
      </div>

      <!-- Email List -->
      <DataTable
        :value="emails"
        :loading="loading"
        dataKey="id"
        :globalFilterFields="['to', 'message.subject', 'message.text']"
        paginator
        :rows="10"
        :rowsPerPageOptions="[10, 20, 50]"
        v-model:filters="tableFilters"
        filterDisplay="menu"
        :sortField="sortField"
        :sortOrder="sortOrder"
        @sort="onSort"
        selectionMode="single"
        @row-click="viewEmail"
        class="cursor-pointer"
      >
        <template #header>
          <div class="flex justify-between items-center">
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText v-model="tableFilters['global'].value" placeholder="Search emails..." />
            </span>
          </div>
        </template>

        <Column field="to" header="To" :sortable="true" />
        <Column field="message.subject" header="Subject" :sortable="true" />
        <Column field="sentAt" header="Sent" :sortable="true">
          <template #body="{ data }">
            {{ formatDate(data.sentAt) }}
          </template>
        </Column>
        <Column field="delivery.state" header="Status" :sortable="true">
          <template #body="{ data }">
            <Tag
              :value="data.delivery?.state || 'SENT'"
              :severity="getStatusSeverity(data.delivery?.state)"
            />
          </template>
        </Column>
      </DataTable>
    </template>
    <template v-else>
      <div class="text-center py-8">
        <p class="text-gray-600 mb-4">No email addresses for this Organization or its contacts</p>
        <Button label="Re-check" text @click="checkForEmailAddresses" :loading="loading" />
      </div>
    </template>

    <!-- Email Composer Dialog -->
    <Dialog
      v-model:visible="showEmailDialog"
      :style="{ width: '80vw' }"
      header="Send Email"
      :modal="true"
      class="p-fluid"
    >
      <EmailComposer
        v-if="showEmailDialog"
        :personId="selectedPerson?.id"
        :organizationId="props.organizationId"
        @sent="onEmailSent"
        @error="onEmailError"
        @cancel="showEmailDialog = false"
      />
    </Dialog>

    <!-- Subscription Management Dialog -->
    <Dialog
      v-model:visible="showSubscriptionDialog"
      :style="{ width: '500px' }"
      :header="getSubscriptionDialogHeader()"
      :modal="true"
      class="p-fluid"
    >
      <div class="space-y-4">
        <div
          v-for="list in emailLists"
          :key="list.id"
          class="flex items-center justify-between p-2 border rounded"
        >
          <div>
            <h4 class="text-lg font-medium">{{ list.name }}</h4>
            <p class="text-sm text-gray-600">{{ list.description }}</p>
          </div>
          <div class="flex items-center gap-2 ml-auto">
            <Button
              v-if="!isSubscribed(list.id || '')"
              icon="pi pi-plus"
              label="Subscribe"
              size="small"
              @click="subscribeToList(list.id || '')"
              :loading="subscribing === list.id"
            />
            <Button
              v-else
              icon="pi pi-times"
              label="Unsubscribe"
              severity="danger"
              size="small"
              @click="unsubscribeFromList(list.id || '')"
              :loading="unsubscribing === list.id"
            />
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Email Detail Dialog -->
    <Dialog
      v-model:visible="showEmailDetailDialog"
      :style="{ width: '80%', maxWidth: '800px' }"
      :modal="true"
      :closable="true"
      header="Email Details"
    >
      <div v-if="selectedEmail">
        <div class="email-details">
          <div class="email-header">
            <div class="field">
              <label>Subject</label>
              <div>{{ selectedEmail.message?.subject }}</div>
            </div>
            <div class="field">
              <label>From</label>
              <div>{{ selectedEmail.from || 'N/A' }}</div>
            </div>
            <div class="field">
              <label>To</label>
              <div>{{ selectedEmail.to }}</div>
            </div>
            <div class="field">
              <label>Sent</label>
              <div>{{ formatDate(selectedEmail.sentAt || null) }}</div>
            </div>
            <div class="field">
              <label>Status</label>
              <Tag
                :value="selectedEmail.delivery?.state || 'SENT'"
                :severity="getStatusSeverity(selectedEmail.delivery?.state)"
              />
            </div>
          </div>
          <div class="email-body">
            <div v-if="selectedEmail.message?.html" v-html="selectedEmail.message.html"></div>
            <div v-else-if="selectedEmail.message?.text">{{ selectedEmail.message.text }}</div>
            <div v-else class="no-content">No content available</div>
          </div>
          <div v-if="selectedEmail.delivery?.info?.messageId" class="email-footer">
            <small>Message ID: {{ selectedEmail.delivery.info.messageId }}</small>
          </div>
        </div>
      </div>
      <template #footer>
        <Button
          label="Close"
          icon="pi pi-times"
          @click="showEmailDetailDialog = false"
          class="p-button-text"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useEmailService } from '@/services/emailService'
import { useEmailListStore } from '@/stores/emailListStore'
import { useEmailSubscriptionService } from '@/services/emailSubscriptionService'
import { useOrganizationStore } from '@/stores/v2/organization'
import { usePersonStore } from '@/stores/v2/person'
import type { Email } from '@/stores/emailStore'
import type { EmailList } from '@/types/email'
import type { DataTableSortEvent } from 'primevue/datatable'
import type { DataTableRowClickEvent } from 'primevue/datatable'
import type { WhereFilterOp } from 'firebase/firestore'

// Components
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Dialog from 'primevue/dialog'
import Tag from 'primevue/tag'
import EmailComposer from '@/components/email/EmailComposer.vue'

const props = defineProps<{
  personId?: string
  organizationId?: string
}>()

const toast = useToast()
const emailService = useEmailService()
const emailListStore = useEmailListStore()
const emailSubscriptionService = useEmailSubscriptionService()
const organizationStore = useOrganizationStore()
const personStore = usePersonStore()

// State
const loading = ref(false)
const emails = ref<Email[]>([])
const showEmailDialog = ref(false)
const showSubscriptionDialog = ref(false)
const showEmailDetailDialog = ref(false)
const emailLists = ref<EmailList[]>([])
const organizationSubscriptions = ref<{ id: string; listId: string; status: string }[]>([])
const personSubscriptions = ref<Record<string, { id: string; listId: string; status: string }[]>>(
  {},
)
const subscribing = ref<string | null>(null)
const unsubscribing = ref<string | null>(null)
const sortField = ref<string>('sentAt')
const sortOrder = ref<number>(-1)
const tableFilters = ref({
  global: { value: null, matchMode: 'contains' },
})
const hasEmailAddresses = ref(false)
const organizationEmail = ref<string | null>(null)
const emailablePeople = ref<
  Array<{ id: string; firstName: string; lastName: string; email: string }>
>([])
const selectedContact = ref<{ type: 'person' | 'organization'; id: string } | null>(null)
const selectedPerson = ref<{
  id: string
  firstName: string
  lastName: string
  email: string
} | null>(null)
const selectedEmail = ref<Email | null>(null)

// Define the subscription type
type Subscription = {
  id: string | undefined
  listId: string
  status: string
}

// Methods
function formatDate(date: Date | null) {
  if (!date) return ''
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

function getStatusSeverity(state?: string): string {
  const severityMap: Record<string, string> = {
    SENT: 'success',
    DELIVERED: 'success',
    FAILED: 'danger',
    RETRY: 'warning',
  }
  return severityMap[state || 'SENT'] || 'info'
}

function onSort(event: DataTableSortEvent) {
  if (typeof event.sortField === 'string') {
    sortField.value = event.sortField
    sortOrder.value = event.sortOrder ?? -1
  }
}

async function loadEmails() {
  if (typeof props.personId === 'string') {
    loading.value = true
    try {
      emails.value = await emailService.getEmailsForPerson(props.personId)
    } catch (error) {
      console.error('Error loading emails:', error)
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load emails',
        life: 5000,
      })
    } finally {
      loading.value = false
    }
  } else if (typeof props.organizationId === 'string') {
    loading.value = true
    try {
      emails.value = await emailService.getEmailsForOrganization(props.organizationId)
    } catch (error) {
      console.error('Error loading emails:', error)
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load emails',
        life: 5000,
      })
    } finally {
      loading.value = false
    }
  }
}

async function loadEmailLists() {
  await emailListStore.load()
  emailLists.value = emailListStore.items
}

async function loadSubscriptions() {
  if (typeof props.personId === 'string') {
    console.log('Loading subscriptions for person:', props.personId)
    const subs = await emailSubscriptionService.getPersonSubscriptions(props.personId)
    console.log('Found subscriptions:', subs)
    personSubscriptions.value = {
      [props.personId]: subs.map((sub) => ({
        id: sub.id!,
        listId: sub.listId,
        status: sub.status,
      })),
    }
  } else if (typeof props.organizationId === 'string') {
    console.log('Loading subscriptions for organization:', props.organizationId)
    const subs = await emailSubscriptionService.getOrganizationSubscriptions(props.organizationId)
    console.log('Found subscriptions:', subs)
    organizationSubscriptions.value = subs.map((sub) => ({
      id: sub.id!,
      listId: sub.listId,
      status: sub.status,
    }))
  }
  console.log(
    'Current subscriptions state:',
    personSubscriptions.value,
    organizationSubscriptions.value,
  )
}

function isSubscribed(listId: string): boolean {
  if (!selectedContact.value) return false

  if (selectedContact.value.type === 'person') {
    return (
      personSubscriptions.value[selectedContact.value.id]?.some(
        (sub) => sub.listId === listId && sub.status === 'active',
      ) || false
    )
  }

  return organizationSubscriptions.value.some(
    (sub) => sub.listId === listId && sub.status === 'active',
  )
}

async function subscribeToList(listId: string) {
  if (!selectedContact.value) return

  subscribing.value = listId
  try {
    let emailAddress = ''
    const contact = selectedContact.value
    if (contact.type === 'person') {
      const person = emailablePeople.value.find((p) => p.id === contact.id)
      emailAddress = person?.email || ''
    } else {
      emailAddress = organizationEmail.value || ''
    }

    if (!emailAddress) {
      throw new Error('No email address found for contact')
    }

    const subscription = await emailSubscriptionService.subscribeToList({
      listId,
      email: emailAddress,
      ...(contact.type === 'person' ? { personId: contact.id } : { organizationId: contact.id }),
    })

    // Update the local state immediately
    if (contact.type === 'person') {
      if (!personSubscriptions.value[contact.id]) {
        personSubscriptions.value[contact.id] = []
      }
      personSubscriptions.value[contact.id].push({
        id: subscription.id!,
        listId,
        status: 'active',
      })
    } else {
      organizationSubscriptions.value.push({
        id: subscription.id!,
        listId,
        status: 'active',
      })
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Subscribed successfully',
      life: 3000,
    })
  } catch (error) {
    console.error('Error subscribing:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error instanceof Error ? error.message : 'Failed to subscribe',
      life: 5000,
    })
  } finally {
    subscribing.value = null
  }
}

async function unsubscribeFromList(listId: string) {
  if (!selectedContact.value) return

  unsubscribing.value = listId
  try {
    await emailSubscriptionService.unsubscribeFromList(
      listId,
      selectedContact.value.type === 'person' ? selectedContact.value.id : undefined,
      selectedContact.value.type === 'organization' ? selectedContact.value.id : undefined,
    )

    // Update the local state immediately
    if (selectedContact.value.type === 'person') {
      const index = personSubscriptions.value[selectedContact.value.id]?.findIndex(
        (sub) => sub.listId === listId,
      )
      if (index !== -1) {
        personSubscriptions.value[selectedContact.value.id][index] = {
          ...personSubscriptions.value[selectedContact.value.id][index],
          status: 'unsubscribed',
        }
      }
    } else {
      const index = organizationSubscriptions.value.findIndex((sub) => sub.listId === listId)
      if (index !== -1) {
        organizationSubscriptions.value[index] = {
          ...organizationSubscriptions.value[index],
          status: 'unsubscribed',
        }
      }
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Unsubscribed successfully',
      life: 3000,
    })
  } catch (error) {
    console.error('Error unsubscribing:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to unsubscribe',
      life: 5000,
    })
  } finally {
    unsubscribing.value = null
  }
}

function onEmailSent(_emailLogId: string) {
  showEmailDialog.value = false
  loadEmails()
}

function onEmailError(error: Error) {
  console.error('Error sending email:', error)
}

function getListName(listId: string): string {
  const list = emailLists.value.find((l) => l.id === listId)
  return list?.name || listId
}

// Update loadEmailableContacts to handle subscription types correctly
async function loadEmailableContacts() {
  if (props.organizationId) {
    const org = await organizationStore.get(props.organizationId)
    organizationEmail.value = org?.email || null

    const people = await emailService.getEmailablePeople(undefined, props.organizationId)
    emailablePeople.value = people.map((p) => {
      const [firstName, lastName] = p.name.split(' ')
      return {
        id: p.id,
        firstName,
        lastName,
        email: p.email,
      }
    })

    // Load subscriptions for organization and people
    const orgSubs = await emailSubscriptionService.getOrganizationSubscriptions(
      props.organizationId,
    )
    organizationSubscriptions.value = orgSubs.map((sub) => ({
      id: sub.id!,
      listId: sub.listId,
      status: sub.status,
    }))

    const subs = await Promise.all(
      people.map(async (person) => {
        const personSubs = await emailSubscriptionService.getPersonSubscriptions(person.id)
        return [
          person.id!,
          personSubs.map((sub) => ({
            id: sub.id!,
            listId: sub.listId,
            status: sub.status,
          })),
        ]
      }),
    )
    personSubscriptions.value = Object.fromEntries(subs)
  } else if (props.personId) {
    const person = await personStore.get(props.personId)
    if (person?.email) {
      emailablePeople.value = [
        {
          id: person.id!,
          firstName: person.firstName,
          lastName: person.lastName,
          email: person.email,
        },
      ]
      // Load subscriptions for this person
      const personSubs = await emailSubscriptionService.getPersonSubscriptions(person.id!)
      personSubscriptions.value = {
        [person.id!]: personSubs.map((sub) => ({
          id: sub.id!,
          listId: sub.listId,
          status: sub.status,
        })),
      }
    }
  }
}

// Update checkForEmailAddresses to also load contacts
async function checkForEmailAddresses() {
  hasEmailAddresses.value = await emailService.hasEmailAddresses(
    props.personId,
    props.organizationId,
  )
  if (hasEmailAddresses.value) {
    await loadEmailableContacts()
  }
}

// Lifecycle
onMounted(async () => {
  if (props.personId || props.organizationId) {
    await checkForEmailAddresses()
    if (hasEmailAddresses.value) {
      await Promise.all([loadEmails(), loadEmailLists(), loadSubscriptions()])
    }
  }
})

// Watch for prop changes
watch(
  () => [props.personId, props.organizationId],
  async ([newPersonId, newOrgId]) => {
    if (newPersonId || newOrgId) {
      await checkForEmailAddresses()
      if (hasEmailAddresses.value) {
        await Promise.all([loadEmails(), loadEmailLists(), loadSubscriptions()])
      }
    } else {
      emails.value = []
      personSubscriptions.value = {}
      organizationSubscriptions.value = []
      hasEmailAddresses.value = false
    }
  },
)

// Add this function to get the subscription dialog header
function getSubscriptionDialogHeader() {
  if (!selectedContact.value) return 'Manage Email Subscriptions'

  if (selectedContact.value.type === 'organization' && organizationEmail.value) {
    return `Manage Subscriptions - ${organizationEmail.value}`
  } else if (selectedContact.value.type === 'person') {
    const person = emailablePeople.value.find((p) => p.id === selectedContact.value?.id)
    if (person) {
      return `Manage Subscriptions - ${person.firstName} ${person.lastName} (${person.email})`
    }
  }
  return 'Manage Email Subscriptions'
}

// Add function to open subscription dialog for a specific contact
function openSubscriptionDialog(type: 'person' | 'organization', id: string) {
  selectedContact.value = { type, id }
  showSubscriptionDialog.value = true
}

// Add function to open email dialog for a specific contact
function openEmailDialog(type: 'person' | 'organization', id: string) {
  if (type === 'person') {
    const person = emailablePeople.value.find((p) => p.id === id)
    if (person) {
      selectedPerson.value = person
    }
  } else {
    selectedPerson.value = null
  }
  showEmailDialog.value = true
}

function viewEmail(event: DataTableRowClickEvent) {
  selectedEmail.value = event.data as Email
  showEmailDetailDialog.value = true
}
</script>

<style scoped>
.email-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.email-header {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 1rem;
}

.email-header .field {
  margin-bottom: 0.5rem;
}

.email-header label {
  font-weight: bold;
  display: block;
  margin-bottom: 0.25rem;
  color: #666;
}

.email-body {
  padding: 1rem 0;
  min-height: 200px;
}

.email-footer {
  border-top: 1px solid #eee;
  padding-top: 0.5rem;
  color: #666;
}

.no-content {
  color: #999;
  font-style: italic;
}
</style>
